import type { ReactNode } from 'react';
import { useEffect } from 'react';
import classNames from 'classnames';
import { Button } from '@common/Button/Button';
import type { CableToolsPostMessage } from '@models/data';
import { getLocale } from '@libs/constants';
import { IGUS_REWRITE } from '@libs/redirect.helper';
import { useIsomorphicLayoutEffect } from '@hooks/useIsomorphicLayoutEffect';
import { useRouter } from 'next/router';
import { useBreakpoints } from '@hooks/useBreakpoints';
import styles from './Modal.module.scss';

export enum ModalVariant {
  DEFAULT,
  ALERT,
  DIALOG,
}

export interface ModalProps {
  visible: boolean;
  headline?: string;
  closable?: boolean;
  onClose(): void;
  onOpen?(): void;
  labels?: {
    closeButtonText: string;
  };
  children?: ReactNode;
  footer?: ReactNode;
  variant?: ModalVariant;
  staticBackdrop?: boolean;
}

export const Modal = ({
  variant = ModalVariant.DEFAULT,
  visible = false,
  headline,
  closable = true,
  onClose,
  onOpen,
  labels = {
    closeButtonText: '',
  },
  children,
  footer,
  staticBackdrop,
}: ModalProps) => {
  const router = useRouter();
  const locale = getLocale(router);
  useEffect(() => {
    window.document.body.classList[visible ? 'add' : 'remove']('overflow-hidden');
  }, [visible]);

  // igus.de iframes (sometimes?) use postMessage to communicate events such
  // as closing a modal.
  // TODO: if we encounter more message events we could replace that with a
  // react hook
  useEffect(() => {
    const messageHandler = (event: MessageEvent<CableToolsPostMessage>) => {
      if (event.data.action === 'close overlay') {
        onClose();
      } else if (event.data.action === 'change url' && window && event.data.url) {
        window.location.assign(
          event.data.url.startsWith('http') ? event.data.url : IGUS_REWRITE(event.data.url, locale)
        );
      }
    };

    window.addEventListener('message', messageHandler);

    // clean up
    return () => window.removeEventListener('message', messageHandler);
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (visible && onOpen) {
      onOpen();
    }
  }, [visible]);

  const { md } = useBreakpoints();

  let width: string | undefined;
  if (variant === ModalVariant.ALERT) {
    width = md ? '575px' : '95%';
  }

  return visible ? (
    <div
      role="dialog"
      data-testid="modal-container"
      className={classNames(
        'flex fixed top-0 left-0 bottom-0 right-0 z-[100]  h-full bg-gray-600 bg-opacity-60 pb-[7.20rem] md:pb-0',
        variant !== ModalVariant.DIALOG ? 'flex-col overflow-y-auto' : 'items-center justify-center',
        visible ? 'visible' : 'hidden'
      )}
    >
      <div
        data-testid="modal-background"
        onClick={staticBackdrop ? undefined : () => onClose()}
        className="absolute top-0 left-0 right-0 bottom-0"
      />
      <div
        className={classNames(
          'bg-white relative py-10 flex flex-col',
          variant !== ModalVariant.DIALOG && 'px-20 sm:pb-24 max-w-full mx-auto my-20',
          md || variant !== ModalVariant.DIALOG ? 'max-w-auto w-auto h-auto' : 'max-w-full w-full h-full'
        )}
        style={{
          maxHeight: md && variant === ModalVariant.DIALOG ? '70vh' : 'none',
          width,
        }}
      >
        {closable && (
          <button
            type="button"
            data-testid="modal-close-icon"
            onClick={onClose}
            className={classNames(
              'absolute top-0 right-0 bg-gray-200 text-white font-bold text-5xl leading-6 p-8',
              styles.ModalCloseButton
            )}
          >
            x
          </button>
        )}

        {headline && (
          <h4
            data-testid="modal-headline"
            className={classNames(
              'text-4xl pb-10',
              variant !== ModalVariant.ALERT && 'px-6',
              variant !== ModalVariant.DIALOG ? 'pt-14' : 'pt-4'
            )}
          >
            {headline}
          </h4>
        )}
        <div
          data-testid="modal-content"
          className={classNames(
            'flex-auto relative',
            variant !== ModalVariant.ALERT && 'px-6',
            variant === ModalVariant.DIALOG && 'overflow-hidden overflow-y-scroll'
          )}
        >
          {children}
        </div>
        {(footer || labels?.closeButtonText) && (
          <div data-testid="modal-footer" className={classNames('pt-10', variant !== ModalVariant.ALERT && 'px-6')}>
            {labels?.closeButtonText && (
              <div className="flex justify-center">
                <Button text={labels.closeButtonText} onClick={onClose} download={false} />
              </div>
            )}
            {footer}
          </div>
        )}
      </div>
    </div>
  ) : null;
};
